import type { CoverageReport, GroupWithSize, KnowledgeBase, OwnerView } from '@ralph/contracts'
import type { I18n, Messages } from '@/i18n/index.ts'

/**
 * The pure half of the builder's Knowledge step (PRD-26 GRP-045, D-16, D-17, D-43, D-46).
 *
 * Everything here answers "given this data, what state is the screen in", and returns a state
 * rather than JSX. Two ideas organise it:
 *
 *   - **Attaching is not sharing.** A maker attaches bases they can use; at answer time each reader
 *     still gets only what they can use — unless the base's managers let it answer through this
 *     agent for everyone it reaches. So every attached base says, for the audience being CHOSEN,
 *     whether everyone gets answers from it, whether it answers through this agent, or who misses
 *     out — and what the maker can do about it: allow it themselves if they maintain the base, or
 *     ask its managers.
 *   - **Coverage is measured on what is saved.** The server reads attachments from the saved
 *     agent, so a base ticked a moment ago has no reading yet; it says "save to see" rather than
 *     pretending to be covered.
 */

export type CoverageEntry = CoverageReport['bases'][number]

// ---------------------------------------------------------------------------
// What the maker can attach
// ---------------------------------------------------------------------------

export type AttachableRow = {
  id: string
  name: string
  owner: OwnerView
  documentCount: number
  storageBytes: number
  status: KnowledgeBase['status']
}

const rowOf = (base: KnowledgeBase): AttachableRow => ({
  id: base.id,
  name: base.name,
  owner: base.owner,
  documentCount: base.documentCount,
  storageBytes: base.storageBytes,
  status: base.status
})

/**
 * *You maintain* and *You can use*, as the Knowledge page names them. The server already splits the
 * two views; a base that somehow appears in both is listed once, under *You maintain*, because the
 * stronger relation is the one worth stating.
 */
export const attachableRows = (input: {
  maintain: readonly KnowledgeBase[]
  use: readonly KnowledgeBase[]
}): { maintain: AttachableRow[]; use: AttachableRow[] } => {
  const maintained = new Set(input.maintain.map((base) => base.id))
  return {
    maintain: input.maintain.map(rowOf),
    use: input.use.filter((base) => !maintained.has(base.id)).map(rowOf)
  }
}

/**
 * Attached bases the maker can no longer find in either list — one that answers through this agent
 * although they cannot use it, or one that stopped being shared with them (D-43 keeps both). They
 * still have to be visible, or nobody could remove them; the coverage report names them when it can.
 */
export const unlistedAttachments = (
  attachedIds: readonly string[],
  listed: { maintain: readonly AttachableRow[]; use: readonly AttachableRow[] },
  coverage: readonly CoverageEntry[]
): Array<{ id: string; name: string | null; owner: OwnerView | null }> => {
  const known = new Set([...listed.maintain, ...listed.use].map((row) => row.id))
  const byId = new Map(coverage.map((entry) => [entry.baseId, entry]))
  return attachedIds
    .filter((id) => !known.has(id))
    .map((id) => {
      const entry = byId.get(id)
      return { id, name: entry?.name ?? null, owner: entry?.owner ?? null }
    })
}

// ---------------------------------------------------------------------------
// Coverage, per attached base
// ---------------------------------------------------------------------------

/** Who will not get answers from a base, in the most specific terms the report allows. */
export type Missing =
  | { kind: 'everyone'; people: number }
  | { kind: 'groups'; groups: GroupWithSize[]; people: number }
  | { kind: 'people'; people: number }

/** What the maker can do about a gap, and where an earlier ask stands. */
export type GapAction =
  /** They maintain the base: they can let it answer through this agent themselves. */
  | { kind: 'lend' }
  /** Nothing asked yet, or the last ask ended without an answer that still holds. */
  | { kind: 'ask'; noMaintainer: boolean }
  | { kind: 'pending'; at: string; noMaintainer: boolean }
  | { kind: 'declined'; at: string; by: string | null; note: string; canAskAgain: boolean; noMaintainer: boolean }

export type CoverageLine =
  | { kind: 'unsaved' }
  | { kind: 'covered' }
  /**
   * It answers through this agent for everyone it reaches. `by` is who allowed it, when known, and
   * `byAdministrator` says it was an administrator acting by role (D-18) — shown, never hidden.
   */
  | { kind: 'lent'; by: string | null; byAdministrator: boolean }
  | { kind: 'gap'; missing: Missing; action: GapAction }

/** A declined asker waits a day before asking again (D-17); the server refuses sooner. */
export const ASK_AGAIN_AFTER_MS = 24 * 60 * 60 * 1000

const missingOf = (entry: CoverageEntry): Missing => {
  if (entry.missing.everyone) return { kind: 'everyone', people: entry.missing.people }
  if (entry.missing.groups.length > 0) {
    return { kind: 'groups', groups: entry.missing.groups, people: entry.missing.people }
  }
  return { kind: 'people', people: entry.missing.people }
}

const actionOf = (entry: CoverageEntry, now: number): GapAction => {
  if (entry.maintainedByCaller) return { kind: 'lend' }
  const request = entry.request
  if (request?.status === 'pending') return { kind: 'pending', at: request.at, noMaintainer: entry.noMaintainer }
  if (request?.status === 'declined') {
    const decided = Date.parse(request.at)
    return {
      kind: 'declined',
      at: request.at,
      by: request.decidedByName,
      note: request.note,
      canAskAgain: Number.isNaN(decided) || now - decided >= ASK_AGAIN_AFTER_MS,
      noMaintainer: entry.noMaintainer
    }
  }
  // Withdrawn, closed, or approved and since stopped: none of them is an answer that still holds.
  return { kind: 'ask', noMaintainer: entry.noMaintainer }
}

/**
 * One attached base's line. `entry` is `undefined` for a base attached in the draft and not saved
 * yet — the report cannot know about it, and the line says so instead of guessing.
 */
export const presentCoverage = (entry: CoverageEntry | undefined, now: number = Date.now()): CoverageLine => {
  if (!entry) return { kind: 'unsaved' }
  if (entry.status === 'covered') return { kind: 'covered' }
  if (entry.status === 'lent') {
    // The lending itself says who allowed it, however it was allowed — through a request or from the
    // base's own page. An approved request is the fallback for a report that does not carry it.
    if (entry.lentBy) return { kind: 'lent', by: entry.lentBy.name, byAdministrator: entry.lentBy.byAdministrator }
    return {
      kind: 'lent',
      by: entry.request?.status === 'approved' ? entry.request.decidedByName : null,
      byAdministrator: false
    }
  }
  return { kind: 'gap', missing: missingOf(entry), action: actionOf(entry, now) }
}

// ---------------------------------------------------------------------------
// The words for a gap
// ---------------------------------------------------------------------------

/**
 * The reader's language, threaded in rather than captured — both halves of it.
 *
 * `Words` is what a sentence needs in order to FORMAT: sizes, counts and joins are all
 * locale-shaped — "1.240" and "Legal e Ops" in Portuguese, "1,240" and "Legal and Ops" in
 * English. `MakerWords` is the catalogue the sentence comes FROM. Neither is imported: this
 * module is pure, and a catalogue branch captured at module scope — `const k = makerStrings…`,
 * which is what stood here — keeps whichever language was live when the bundle evaluated and
 * then renders it for the rest of the session, with no type error and no failing test
 * (PRD-27 LOC-020, F-3). Narrow picks, so a call site can see from the signature what a
 * sentence will format.
 */
type Words = Pick<I18n, 'locale' | 'formatCount' | 'formatList'>
type MakerWords = Messages['marketplace']['maker']

/** "Legal (8 people)", "Legal (8 people) and Ops (4 people)". */
export const groupsWithSizes = (t: MakerWords, fmt: Words, groups: readonly GroupWithSize[]): string =>
  fmt.formatList(groups.map((group) => t.sized(fmt, group)))

/** The coverage line's sentence about who misses out (the Knowledge step). */
export const missingSentence = (t: MakerWords, fmt: Words, missing: Missing): string => {
  const k = t.sections.knowledge
  if (missing.kind === 'everyone') return k.coverage.missingEveryone(fmt, missing.people)
  if (missing.kind === 'groups') return k.coverage.missingGroups(groupsWithSizes(t, fmt, missing.groups))
  return k.coverage.missingPeople(fmt, missing.people)
}

/** Who misses out, as the object of "will not reach …" (the summary and the consent sentence). */
export const missingWhom = (t: MakerWords, fmt: Words, missing: Missing): string => {
  const a = t.sections.audience
  if (missing.kind === 'everyone') return a.gapEveryone(fmt, missing.people)
  if (missing.kind === 'groups') return groupsWithSizes(t, fmt, missing.groups)
  return a.gapPeople(fmt, missing.people)
}

export type CoverageGap = { baseId: string; base: string; whom: string }

/**
 * Every attached base with a gap, in report order: the summary under "Who can use it" ("Payroll
 * Rules will not reach Legal (8 people) — see Knowledge") and the gaps the consent sentence states.
 */
export const coverageGaps = (t: MakerWords, fmt: Words, entries: readonly CoverageEntry[]): CoverageGap[] =>
  entries.flatMap((entry) => {
    const line = presentCoverage(entry)
    return line.kind === 'gap'
      ? [{ baseId: entry.baseId, base: entry.name, whom: missingWhom(t, fmt, line.missing) }]
      : []
  })

/** People the agent reaches who cannot use a base — what the decision to let it answer is about. */
export const peopleBeyond = (entry: CoverageEntry | undefined): number => entry?.missing.people ?? 0

// ---------------------------------------------------------------------------
// Space
// ---------------------------------------------------------------------------

/**
 * How much a base holds, in words a maker asked for none of the units of.
 *
 * The maker plane may not speak in bytes (AGM-022), and the reason is not squeamishness: the only
 * decision this number supports is "am I near my limit", for which "under 1 MB" is a complete
 * answer and "812,447" is a puzzle. The unit suffixes are a spec rather than prose and stay
 * hand-written; the sentence below the smallest tier is prose, and comes from the catalogue.
 */
export const formatSpace = (t: MakerWords, fmt: Pick<I18n, 'formatNumber'>, bytes: number): string => {
  // The tiers are the ones this product already showed, and the number inside them now carries
  // the reader's decimal mark: "3.2 MB" in English, "3,2 MB" in Portuguese. `minimumFractionDigits`
  // because `toFixed(1)` always printed a decimal — 3 MB read "3.0 MB" and still does.
  const oneDecimal = (value: number) => fmt.formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const megabytes = bytes / (1024 * 1024)
  // The one tier that is prose rather than a number with a unit, so it lives in the catalogue.
  if (megabytes < 1) return t.sections.knowledge.underOneMegabyte
  if (megabytes < 10) return `${oneDecimal(megabytes)} MB`
  // No grouping here on purpose: this tier tops out at 1023 MB, and a separator appearing at
  // 1,000 MB would be the one place this migration moved an English rendering.
  if (megabytes < 1024) return `${fmt.formatNumber(megabytes, { maximumFractionDigits: 0, useGrouping: false })} MB`
  return `${oneDecimal(megabytes / 1024)} GB`
}
